import { GlassCard } from '@/components/design/GlassCard';
import { NeonButton } from '@/components/design/NeonButton';
import { Plus, Search, X, Eye, Edit2, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { AdminAPI } from '@/services/api';

export const BlogListPage = () => {
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  // Fetch posts on mount
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { data } = await AdminAPI.content.getPosts();
        setPosts(data);
      } catch (error) {
        console.error('Failed to fetch posts', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  /*
   * Axtarış: bloqun adı (hər iki dildə), müəllif adı və statusu üzrə süzgəc.
   * Əvvəl burada işləməyən «Filter» düyməsi var idi — sahə indi yazdıqca süzür.
   */
  const filteredPosts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter((post) =>
      [post.titleAz, post.titleEn, post.author?.name, post.published ? 'published' : 'draft']
        .filter(Boolean)
        .some((field: string) => field.toLowerCase().includes(q))
    );
  }, [posts, query]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">Blog Posts</h2>
          <p className="text-muted-foreground">Manage your articles and publications.</p>
        </div>
        <Link to="/blog/new">
          <NeonButton>
            <Plus className="w-4 h-4 mr-2" />
            Create Post
          </NeonButton>
        </Link>
      </div>

      <GlassCard className="p-0 overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-white/10 flex items-center gap-4 bg-white/5">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              className="w-full bg-background/50 border border-white/10 rounded-lg pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="Search by title, author or status..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {query && (
            <span className="text-sm text-muted-foreground">
              {filteredPosts.length} of {posts.length}
            </span>
          )}
        </div>

        {/* Table */}
        <div className="w-full overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-muted-foreground uppercase bg-white/5">
              <tr>
                <th className="px-6 py-4 font-medium">Title</th>
                <th className="px-6 py-4 font-medium">Author</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Views</th>
                <th className="px-6 py-4 font-medium">Date</th>
                <th className="px-6 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                <tr>
                   <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">Loading posts...</td>
                </tr>
              ) : filteredPosts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                    {query
                      ? `No posts match “${query}”.`
                      : 'No posts found. Create one to get started.'}
                  </td>
                </tr>
              ) : (
                filteredPosts.map((post) => (
                  <tr key={post.id} className="group hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 font-medium text-white">{post.titleEn || post.titleAz || 'Untitled'}</td>
                    <td className="px-6 py-4 text-muted-foreground">{post.author?.name || 'Unknown'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        post.published 
                          ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                          : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                      }`}>
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">0</td> {/* View Tracking not yet in Backend */}
                    <td className="px-6 py-4 text-muted-foreground">
                      {new Date(post.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button className="p-2 hover:bg-white/10 rounded-lg transition-colors text-muted-foreground hover:text-white">
                          <Eye className="w-4 h-4" />
                        </button>
                        <Link to={`/blog/edit/${post.id}`}>
                          <button className="p-2 hover:bg-white/10 rounded-lg transition-colors text-muted-foreground hover:text-white">
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </Link>
                        <button 
                          onClick={() => AdminAPI.content.deletePost(post.id).then(() => setPosts(posts.filter(p => p.id !== post.id)))}
                          className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-muted-foreground hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
};
